import React, { createContext, useContext, useEffect, useState } from 'react'
import api from '../lib/api'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Role → route mapping
export const ROLE_ROUTES = {
  super_admin: '/super-admin-dashboard',
  vendor: '/gym-owner-portal',
  staff: '/staff-trainer-interface',
  trainer: '/trainer-dashboard',
  member: '/member-portal',
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Restore session from stored JWT on mount
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) {
      setLoading(false)
      return
    }
    api.get('/auth/me')
      .then(({ data }) => {
        setUser(data)
        setProfile(data)
      })
      .catch(() => {
        localStorage.removeItem('token')
      })
      .finally(() => setLoading(false))
  }, [])

  // Sign in
  const signIn = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password })
      localStorage.setItem('token', data.token)
      setUser(data.user)
      setProfile(data.user)
      return { data, error: null }
    } catch (err) {
      return { data: null, error: { message: err.response?.data?.message || 'Network error. Please try again.' } }
    }
  }

  // Sign up
  const signUp = async ({ email, password, fullName, gymName, phone, role = 'vendor' }) => {
    try {
      const { data } = await api.post('/auth/register', {
        email, password, fullName, gymName, phone, role,
      })
      localStorage.setItem('token', data.token)
      setUser(data.user)
      setProfile(data.user)
      return { data, error: null }
    } catch (err) {
      return { data: null, error: { message: err.response?.data?.message || 'Network error. Please try again.' } }
    }
  }

  // Sign out
  const signOut = async () => {
    try {
      await api.post('/auth/logout')
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      localStorage.removeItem('token')
      setUser(null)
      setProfile(null)
    }
    return { error: null }
  }

  // Send password reset email
  const sendPasswordReset = async (email) => {
    try {
      await api.post('/auth/forgot-password', { email })
      return { data: {}, error: null }
    } catch (err) {
      return { data: null, error: { message: err.response?.data?.message || 'Network error. Please try again.' } }
    }
  }

  // Update password using reset token (called from reset-password page)
  const updatePassword = async (newPassword, token) => {
    try {
      await api.post('/auth/reset-password', { token, newPassword })
      return { data: {}, error: null }
    } catch (err) {
      return { data: null, error: { message: err.response?.data?.message || 'Network error. Please try again.' } }
    }
  }

  // Change password (requires current password, user must be logged in)
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.patch('/auth/change-password', { currentPassword, newPassword })
      return { data: {}, error: null }
    } catch (err) {
      return { data: null, error: { message: err.response?.data?.message || 'Network error. Please try again.' } }
    }
  }

  // Resend verification — no email verification in new backend
  const resendVerification = async () => {
    return { data: {}, error: null }
  }

  // Update profile
  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data } = await api.patch('/profiles/me', updates)
      setProfile(prev => ({ ...prev, ...data }))
      return { data, error: null }
    } catch (err) {
      return { data: null, error: { message: err.response?.data?.message || 'Network error. Please try again.' } }
    }
  }

  const userRole = profile?.role || null
  const roleRoute = userRole ? ROLE_ROUTES?.[userRole] : '/login-page'

  const value = {
    user,
    profile,
    loading,
    profileLoading: false,
    isAuthenticated: !!user,
    isEmailVerified: true, // No email verification in Fastify backend
    userRole,
    roleRoute,
    signUp,
    signIn,
    signOut,
    sendPasswordReset,
    updatePassword,
    changePassword,
    resendVerification,
    updateProfile,
    // Legacy compat
    userProfile: profile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
